import {Logger} from '../../class/helper/logger.class';
import {Loader} from '../../models/loader/loader.redux';
import {ServiceAction, ServiceState} from 'src/app/models/service/service.redux';
import {cloneDeep} from 'lodash';
import {ActivatedRoute, Params} from '@angular/router';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {IRecordEmit} from '../../interfaces/record';
import {IRecordHistoryBiochemists, RecordBiochemistAction, RecordHistoryBiochemistsState} from '../../models/record-history-biochemists';
import {AuthState} from 'src/app/models/auth/auth.redux';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {IUser} from '../../models/auth';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {StorageService} from '../../services/storage/storage.service';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-biochemists',
  templateUrl: './record-history-biochemists.component.html',
  styleUrls: ['./record-history-biochemists.component.scss']
})
export class RecordHistoryBiochemistsComponent implements OnInit, OnDestroy {

  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(RecordHistoryBiochemistsState.getWasDeployed);

  @Input() recordHistoryBiochemists: IRecordHistoryBiochemists;
  @Output() recordHistoryBiochemistsChange = new EventEmitter<IRecordEmit>();

  loadInfo: boolean;
  form: UntypedFormGroup;

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;

  userLogged: IUser;

  wasDeployedSubscription: Subscription;
  skeletonTemplate: any[] = [];
  constructor(
    private store: Store,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private messageAlert: MessageAlertService,
    private skeletonService: SkeletonService,
  ) {
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordHistoryBiochemists) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnInit(): void {
    this.initValues();
  }

  initValues(): void {
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS)
    this.form = new UntypedFormGroup({});
  }

  async initValuesHttp(): Promise<void> {
    try {
      // await firstValueFrom(this.store.dispatch(new Loader(true, 'Cargando afectividad de entornos ...')));
      this.getParams();
      this.userLogged = cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordBiochemistAction.GetActive(this.serviceId, this.patientId)));
      this.recordHistoryBiochemists = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordHistoryBiochemistsState.getRecordHistoryBiochemists)));
      this.recordHistoryBiochemistsChange.emit({ record: this.recordHistoryBiochemists, updated: false });

      this.setFormProperties(this.recordHistoryBiochemists);
      this.loadInfo = true;
    } catch (e) {
      Logger.log('Ocurrio un error', e);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  setFormProperties(recordHistoryBiochemists: IRecordHistoryBiochemists): void {
    // this.disableForm = !this.userLogged?.experts;

    this.form = new UntypedFormGroup({
      erythrocytes: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.erythrocytes || 0, []),
        unit: new UntypedFormControl('millones/uL', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(4.50, []),
          max: new UntypedFormControl(5.40, [])
        })
      }),
      hemoglobin: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.hemoglobin || 0, []),
        unit: new UntypedFormControl('gr/dL', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(12.00, []),
          max: new UntypedFormControl(16.00, [])
        })
      }),
      hematocrit: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.hematocrit || 0, []),
        unit: new UntypedFormControl('%', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(38.00, []),
          max: new UntypedFormControl(47.00, [])
        })
      }),

      leukocytes: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.leukocytes || 0, []),
        unit: new UntypedFormControl('miles/uL', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(3.84, []),
          max: new UntypedFormControl(9.79, [])
        })
      }),
      lymphocytes: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.lymphocytes || 0, []),
        unit: new UntypedFormControl('%', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(15.50, []),
          max: new UntypedFormControl(48.6, [])
        })
      }),
      serum_creatinine: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.serum_creatinine || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(4.20, []),
          max: new UntypedFormControl(5.40, [])
        })
      }),

      serum_urea: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.serum_urea || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(16.00, []),
          max: new UntypedFormControl(46.00, [])
        })
      }),
      urea_nitrogen: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.urea_nitrogen || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(7, []),
          max: new UntypedFormControl(18, [])
        })
      }),
      uric_acid: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.uric_acid || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(3.5, []),
          max: new UntypedFormControl(7.2, [])
        })
      }),

      sodium: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.sodium || 0, []),
        unit: new UntypedFormControl('mmol/L', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(136, []),
          max: new UntypedFormControl(145, [])
        })
      }),
      potassium: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.potassium || 0, []),
        unit: new UntypedFormControl('mmol/L', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(3.5, []),
          max: new UntypedFormControl(5.1, [])
        })
      }),
      calcium: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.calcium || 0, []),
        unit: new UntypedFormControl('ng/dL', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(8.5, []),
          max: new UntypedFormControl(10.1, [])
        })
      }),

      chlorine: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.chlorine || 0, []),
        unit: new UntypedFormControl('mmol/L', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(98, []),
          max: new UntypedFormControl(107, [])
        })
      }),
      serum_magnesium: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.serum_magnesium || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(1.8, []),
          max: new UntypedFormControl(2.4, [])
        })
      }),
      glucose: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.glucose || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(74, []),
          max: new UntypedFormControl(106, [])
        })
      }),

      phosphorus: new UntypedFormGroup({
        value: new UntypedFormControl(recordHistoryBiochemists?.phosphorus || 0, []),
        unit: new UntypedFormControl('mg/dl', []),
        reference: new UntypedFormGroup({
          min: new UntypedFormControl(2.6, []),
          max: new UntypedFormControl(4.7, [])
        })
      }),
    });
    if (!this.userLogged.experts) {
      this.form.disable();
    }
  }

  emitChangeUpdate(): void {
    this.recordHistoryBiochemists.updatedAt = new Date();
    this.recordHistoryBiochemistsChange.emit({ record: this.recordHistoryBiochemists, updated: true });
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando historial ...')));
      const data = {};
      const rawValue = this.form.getRawValue();
      // tslint:disable-next-line:forin
      for (const prop in rawValue) {
        data[prop] = rawValue[prop]?.value;
      }
      await firstValueFrom(this.store.dispatch(new RecordBiochemistAction.PatchActive(
        this.serviceId, this.patientId, data as IRecordHistoryBiochemists)
      ));
      this.messageAlert.createMessage('success', 'Historial bioquímico guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar el historial bioquímico. Intente más tarde', 5000);

      this.recordHistoryBiochemists = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordHistoryBiochemistsState.getRecordHistoryBiochemists)));
      this.setFormProperties(this.recordHistoryBiochemists);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  ngOnDestroy(): void {
    this.wasDeployedSubscription.unsubscribe();
  }
}
